import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { Drink } from "../db";

export const syncProperties = ["sessionMaxPermille", "DrunkDrinks"];

export type DrinkCacheRecord = {
  id: number;
  cachedAt: number;
  lastUsedAt?: number;
  drink: Drink;
};

export type DrinkCache = {
  drinks: DrinkCacheRecord[];
};

const cacheDrinkImages = (drinks: DrinkCache["drinks"]) => {
  if ("serviceWorker" in navigator)
    navigator.serviceWorker.ready.then((registration) => {
      registration.active?.postMessage({
        type: "CACHE_DRINK_IMAGES",
        imageNames: drinks.map((d) => d.drink.icon),
      });
    });
};

const cleanUpCache = (drinks: DrinkCache["drinks"]) => {
  let newDrinks = _.cloneDeep(drinks);
  newDrinks = newDrinks.filter(
    (record) =>
      (record.lastUsedAt ?? record.cachedAt) >
      new Date().getTime() - 1000 * 60 * 60 * 24 * 7
  );
  newDrinks = newDrinks
    .sort((a, b) => (b.lastUsedAt ?? b.cachedAt) - (a.lastUsedAt ?? a.cachedAt))
    .slice(0, 30);
  return newDrinks;
};

const drinkCacheSlice = createSlice({
  name: "drinkCache",
  initialState: {
    drinks: [],
  } as DrinkCache,
  reducers: {
    cacheDrink: (state, action) => {
      const { drink }: { drink: Drink } = action.payload;
      if (_.isString(drink.id))
        throw new Error("Cannot cache drink with string id");
      if (state.drinks.find((d) => d.id === drink.id)) return;
      const cachedAt = new Date().getTime();
      state.drinks.push({
        id: drink.id,
        cachedAt,
        drink,
      });

      state.drinks = cleanUpCache(state.drinks);

      const cachedDrinksCopy = _.cloneDeep(state.drinks);
      cacheDrinkImages(cachedDrinksCopy);
    },
    setDrinkUsed: (state, action) => {
      const { id }: { id: number } = action.payload;
      const drink = state.drinks.find((drink) => drink.id === id);
      if (!drink) return;
      drink.lastUsedAt = new Date().getTime();
    },
    removeCachedDrink: (state, action) => {
      const { id }: { id: number } = action.payload;
      state.drinks = state.drinks.filter((drink) => drink.id !== id);

      state.drinks = cleanUpCache(state.drinks);

      const cachedDrinksCopy = _.cloneDeep(state.drinks);
      cacheDrinkImages(cachedDrinksCopy);
    },
  },
});

export const { cacheDrink, setDrinkUsed, removeCachedDrink } =
  drinkCacheSlice.actions;

export default drinkCacheSlice.reducer;
